import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { homeAudio } from "@/lib/homeAudio";
import { Play, Pause, Volume2, VolumeX, ChevronLeft, ChevronRight, MapPin } from "lucide-react";

const IMAGES = [
  "https://images.unsplash.com/photo-1758599669406-d5179ccefcb9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHBlb3BsZSUyMGNvbW11bml0eSUyMHZvbHVudGVlcnxlbnwwfHx8fDE3ODQzMDQ5MTB8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1758599668299-beebedfabf7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHx2b2x1bnRlZXJzJTIwY2xlYW5pbmclMjBwYXJrfGVufDB8fHx8MTc4NDMwNDkwOXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1696079196661-a5cbfb884255?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMGJlYXV0aWZ1bCUyMGNpdHklMjBwYXJrfGVufDB8fHx8MTc4NDMwNDkwOXww&ixlib=rb-4.1.0&q=85",
];

// Lotto 14 — Home ambient audio track. Array-based so future tracks can be
// added easily (playlist / random pick / A-B testing). Only track[0] is
// currently used. Never bundle audio in the JS: served as a static asset.
const AUDIO_TRACKS = [
  {
    id: "green-awakening",
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_citizen-action-2/artifacts/9jynn5c9_The%20Green%20Awakening.mp3",
    initialVolume: 0.55,
  },
];

/**
 * Hero carousel — Lotto 2:
 * - Mute stays as a small discrete icon on the media (bottom-right), never
 *   over the subject or CTAs.
 * - Bottom control bar contains: prev/play/next on the left AND the primary
 *   CTA "Entra in ToClean" on the right (replacing the previously-planned
 *   audio slot).
 * - Secondary links "La nostra missione" and "Chi siamo" are rendered as
 *   discreet text-links inside the media overlay (below the subtitle) so they
 *   remain accessible without stealing space from the primary CTA.
 */
export default function HeroVideo({ children }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(() => homeAudio.getMuted());
  const timerRef = useRef(null);

  // Lotto 14/15 — Ambient audio: SINGLE persistent instance across the
  // whole tab lifetime, kept alive outside the React tree by
  // /app/frontend/src/lib/homeAudio.js. This component only orchestrates
  // playback (fade-in on first mount, mute toggle, fade-out on "Entra in
  // ToClean"). Route changes to /chi-siamo, /missione, back to /, or
  // scrolling between HomeSwipe screens no longer restart the track.
  const track = AUDIO_TRACKS[0];
  const targetVolume = track.initialVolume;

  // On first mount only: init the global audio element and try to autoplay.
  // Subsequent re-mounts (e.g. user leaves /chi-siamo back to /) are no-ops
  // because homeAudio.init() is idempotent and the audio is already playing.
  useEffect(() => {
    homeAudio.init(track.src);
    // Ensure mute UI state is in sync with the underlying element.
    const unsub = homeAudio.subscribeMuted((m) => setMuted(m));
    homeAudio.setMuted(homeAudio.getMuted());
    // Try immediate audible playback + fade-in. Autoplay may be blocked;
    // if so we fall back to a one-shot user-gesture listener.
    homeAudio.ensurePlayingWithFade(targetVolume, 800);
    if (!homeAudio.hasStartedByGesture()) {
      const onFirstGesture = () => {
        homeAudio.markStartedByGesture();
        homeAudio.setMuted(false);
        homeAudio.ensurePlayingWithFade(targetVolume, 800);
      };
      window.addEventListener("pointerdown", onFirstGesture, { once: true, capture: true });
      window.addEventListener("keydown", onFirstGesture, { once: true, capture: true });
      window.addEventListener("touchstart", onFirstGesture, { once: true, capture: true, passive: true });
      return () => {
        unsub();
        window.removeEventListener("pointerdown", onFirstGesture, true);
        window.removeEventListener("keydown", onFirstGesture, true);
        window.removeEventListener("touchstart", onFirstGesture, true);
        // NOTE: we intentionally DO NOT pause the audio here. Unmounting
        // this component (navigating to /chi-siamo, /missione, or when
        // HomeSwipe re-renders) must keep the track playing seamlessly.
      };
    }
    return () => { unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click handler for the discrete mute/unmute button. Flips the muted flag
  // on the global element — currentTime is preserved so continuity is
  // guaranteed across taps and route changes.
  const onMuteToggleClick = useCallback(() => {
    // If autoplay was blocked, this user gesture is the perfect moment
    // to (re)start playback.
    if (homeAudio.isPaused()) {
      homeAudio.markStartedByGesture();
      homeAudio.ensurePlayingWithFade(targetVolume, 800);
    }
    homeAudio.setMuted(!homeAudio.getMuted());
  }, [targetVolume]);

  // Smooth fade-out + stop before entering the map. This is the ONLY
  // place the audio is intentionally stopped.
  const enterApp = useCallback((e) => {
    e.preventDefault();
    homeAudio.fadeOutAndStop(420).then(() => navigate("/map"));
  }, [navigate]);

  const SLIDES = [
    { img: IMAGES[0], title: t("hero_slide1_title"), subtitle: t("hero_slide1_sub") },
    { img: IMAGES[1], title: t("hero_slide2_title"), subtitle: t("hero_slide2_sub") },
    { img: IMAGES[2], title: t("hero_slide3_title"), subtitle: t("hero_slide3_sub") },
  ];

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => setI((n) => (n + 1) % SLIDES.length), 5500);
    return () => clearTimeout(timerRef.current);
  }, [i, paused]);

  const next = () => setI((n) => (n + 1) % SLIDES.length);
  const prev = () => setI((n) => (n - 1 + SLIDES.length) % SLIDES.length);
  const slide = SLIDES[i];

  return (
    <section className="relative overflow-hidden bg-black text-white w-full h-full flex flex-col" data-testid="hero-video">
      {/* Lotto 14 — Persistent ambient audio is now managed globally via
          /src/lib/homeAudio.js. The <audio> element lives outside the React
          tree so it survives Landing/HomeSwipe/HeroVideo remounts caused by
          navigation to /chi-siamo, /missione, etc. */}
      {/* --- Media area (fills all vertical space above the control bar) --- */}
      <div className="relative flex-1 min-h-0">
        {SLIDES.map((s, idx) => (
          <div
            key={idx}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: idx === i ? 1 : 0, pointerEvents: idx === i ? "auto" : "none" }}
            aria-hidden={idx !== i}
          >
            <img
              src={s.img}
              alt={s.title}
              className="absolute inset-0 w-full h-full object-cover object-center"
              style={{ transform: idx === i && !paused ? "scale(1.02)" : "scale(1)", transition: "transform 8s ease-out" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/5" />
          </div>
        ))}
        {/* Dots — floated near top so they don't collide with the discrete
            mute icon at bottom-right. */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          {SLIDES.map((_, idx) => (
            <button
              key={idx}
              data-testid={`hero-dot-${idx}`}
              onClick={() => setI(idx)}
              className="h-1.5 rounded-full transition-all"
              style={{ width: idx === i ? 30 : 16, background: idx === i ? "#10B981" : "rgba(255,255,255,0.45)" }}
              aria-label={`Slide ${idx + 1}`}
            />
          ))}
        </div>
        {/* Discrete mute/unmute — small, semi-transparent, bottom-right on the
            media. z-30 keeps it above the title/subtitle overlay (which is at
            z-10 and, due to DOM order, was previously stealing clicks on the
            bottom-right corner). We also add pointer-events-none to the title
            wrapper so it never eats taps on the mute button. */}
        <button
          data-testid="hero-mute-toggle"
          type="button"
          onClick={onMuteToggleClick}
          onTouchEnd={(e) => { e.preventDefault(); onMuteToggleClick(); }}
          aria-label={muted ? t("hero_mute_on") : t("hero_mute_off")}
          className="absolute bottom-3 right-3 z-30 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white/90 transition-colors touch-manipulation cursor-pointer"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        {/* Title/subtitle overlay + secondary text-links. pointer-events-none
            on the wrapper so the mute button below-right stays fully clickable
            even where the overlay's bounding box extends. Interactive
            children re-enable pointer-events. */}
        <div className="absolute inset-x-0 bottom-4 sm:bottom-8 z-10 px-5 md:px-16 max-w-4xl pointer-events-none">
          <div key={`t-${i}`} className="font-heading text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] fade-in text-white">
            {slide.title}
          </div>
          <div key={`s-${i}`} className="mt-2 sm:mt-3 text-sm sm:text-base md:text-xl text-white/90 max-w-2xl fade-in line-clamp-2 sm:line-clamp-none">
            {slide.subtitle}
          </div>
          {/* Secondary links styled as compact PILL BUTTONS — Lotto 6 #2.
              They no longer read as plain text-links: rounded 999px, subtle
              backdrop, thin border, small icon slot. Position unchanged
              (just below the subtitle inside the media overlay). */}
          <div className="mt-3 sm:mt-4 flex items-center flex-wrap gap-2 pointer-events-auto">
            <Link
              to="/missione"
              data-testid="hero-cta-mission"
              className="inline-flex items-center gap-1.5 h-8 sm:h-9 px-3 sm:px-4 rounded-full bg-white/12 hover:bg-white/22 backdrop-blur-md border border-white/25 text-white text-xs sm:text-sm font-semibold transition-colors"
            >
              {t("la_nostra_missione")}
            </Link>
            <Link
              to="/chi-siamo"
              data-testid="hero-cta-about"
              className="inline-flex items-center gap-1.5 h-8 sm:h-9 px-3 sm:px-4 rounded-full bg-white/12 hover:bg-white/22 backdrop-blur-md border border-white/25 text-white text-xs sm:text-sm font-semibold transition-colors"
            >
              {t("chi_siamo")}
            </Link>
          </div>
          {children && <div className="mt-4 sm:mt-6">{children}</div>}
        </div>
      </div>

      {/* --- Bottom control bar — outside the media, always visible --- */}
      <div
        data-testid="hero-control-bar"
        className="shrink-0 h-14 sm:h-16 bg-slate-900/95 backdrop-blur-md border-t border-white/10 flex items-center justify-between gap-2 px-3 sm:px-4"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Media controls (compact, on the left) */}
        <div className="flex items-center gap-0.5">
          <button data-testid="hero-prev" onClick={prev} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/90" aria-label={t("hero_prev")}><ChevronLeft size={16} /></button>
          <button data-testid="hero-play-toggle" onClick={() => setPaused((p) => !p)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/90" aria-label={paused ? t("hero_play") : t("hero_pause")}>
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          <button data-testid="hero-next" onClick={next} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/90" aria-label={t("hero_next")}><ChevronRight size={16} /></button>
        </div>
        {/* Primary CTA — "Entra in ToClean" (Lotto 6 #1: sized down so it stays
            proportional to the compact control bar and doesn't dominate the
            hero on small phones). */}
        <Link
          to="/map"
          onClick={enterApp}
          data-testid="hero-cta-enter"
          className="inline-flex items-center gap-1 h-9 px-3 sm:px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[13px] sm:text-sm font-semibold border border-emerald-500/60 shadow-md shadow-emerald-950/20 transition-colors"
        >
          <MapPin size={13} />
          <span>{t("swipe_cta_enter")}</span>
        </Link>
      </div>
    </section>
  );
}
